import { AccessChangeTypeTag } from './AccessChangeTypeTag';
import { AppDisplay } from '@ui/applications/AppDisplay';
import classNames from 'classnames';
import { ColumnType } from 'antd/es/table';
import { ComplianceCategories } from '@ui/icons/ComplianceCategories';
import { DataSecurityAlert } from '@sonar-software/sonar-core';
import { DataSensitivityLevelTag } from '@ui/tags/DataSensitivityLevelTag';
import { getTimeRecordDate } from '@sonar-software/sonar-ts-lib';
import { NavigateFunction } from 'react-router';
import React from 'react';
import { SonarText } from '@ui/text/SonarText';
import { When } from 'react-if';

import { Button, Space, Tooltip } from 'antd';
import { COPY_SPECIFIC, FULL_ROUTES } from '@sonar-software/sonar-ts-constants';

type ColumnDefinition = ColumnType<DataSecurityAlert>;
type SetAlertHidden = (id: number, hide: boolean) => void;

export const typeColumn: ColumnDefinition = {
	dataIndex: 'monitorType',
	key: 'monitorType',
	render: (type: number) => <AccessChangeTypeTag changeType={ type } />,
	title: COPY_SPECIFIC.TYPE,
	width: 100
};

export const appColumn: ColumnDefinition = {
	dataIndex: 'applicationId',
	key: 'applicationId',
	render: (applicationId: number, record: DataSecurityAlert) =>
		<AppDisplay
			appId={ applicationId }
			appLabel={ record.applicationLabel }
			appName={ record.applicationName } />,
	title: COPY_SPECIFIC.APPLICATION,
	width: 170
};

export const titleColumn: ColumnDefinition = {
	dataIndex: 'message',
	key: 'message',
	render: (_, { complianceCategory, dataSensitivityLevelId, hidden, message }) =>
		<div className={ classNames('message', { hidden }) }>
			<Tooltip
				mouseEnterDelay={ 0.5 }
				title={ message }>
				<SonarText>{ message }</SonarText>
			</Tooltip>
			<div className='message-tags'>
				<When condition={ complianceCategory }>
					<ComplianceCategories
						categoryFlags={ complianceCategory }
						direction='horizontal' />
				</When>
				<When condition={ dataSensitivityLevelId }>
					<DataSensitivityLevelTag dataSensitivityLevelId={ dataSensitivityLevelId } />
				</When>
				<When condition={ hidden }>
					<div className='hidden-text'>{ COPY_SPECIFIC.HIDDEN }</div>
				</When>
			</div>
		</div>,
	title: COPY_SPECIFIC.TITLE
};

export const dateColumn: ColumnDefinition = {
	align: 'right',
	dataIndex: 'detectedDate',
	key: 'detectedDate',
	render: (date: string) => <SonarText>{ getTimeRecordDate(date) }</SonarText>,
	sorter: true,
	title: COPY_SPECIFIC.DETECTED_DATE,
	width: 140
};

export const actionColumn = (
	setHidden: SetAlertHidden,
	navigate: NavigateFunction,
	isReadOnly: boolean,
	appSelected: boolean
): ColumnDefinition => ({
	dataIndex: 'hidden',
	key: 'hidden',
	render: (hidden: boolean, { id, applicationId }) => (hidden ?
		<Button onClick={ setHidden.bind(null, id, false) }>{ COPY_SPECIFIC.RESTORE }</Button> :
		<Space size={ 8 }>
			<When condition={ !appSelected }>
				<Button
					onClick={ () => navigate(`${FULL_ROUTES.DATA_SECURITY}/${applicationId}`) }
					type='primary'>
					{ COPY_SPECIFIC.REVIEW }
				</Button>
			</When>
			<When condition={ !isReadOnly }>
				<Button onClick={ setHidden.bind(null, id, true) }>{ COPY_SPECIFIC.HIDE }</Button>
			</When>
		</Space>
	),

	title: COPY_SPECIFIC.ACTION,
	width: appSelected || isReadOnly ? 100 : 200
});

export function getColumns(
	appSelected: boolean,
	isReadOnly: boolean,
	navigate: NavigateFunction,
	setHidden: SetAlertHidden
): Array<ColumnDefinition> {
	return [
		typeColumn,
		appColumn,
		titleColumn,
		dateColumn,
		...isReadOnly && appSelected ? [] : [ actionColumn(setHidden, navigate, isReadOnly, appSelected) ]
	];
}
